<script setup>
import { computed } from "vue";

const measurements = {
  front: {
    neckHeight: 2.1,
    legendLeft: {
      title: "3”",
      description: "XS-XL"
    },
    legendRight: {
      title: "4”",
      description: "2XL+"
    }
  },
  back: {
    neckHeight: 3.5,
    legendLeft: {
      title: "4”",
      description: "XS-XL"
    },
    legendRight: {
      title: "5”",
      description: "2XL+"
    }
  },
  frontLeftChest: {
    neckHeight: 3.5,
    legendLeft: {
      title: "3”",
      description: "XS-XL"
    },
    legendRight: {
      title: "4”",
      description: "2XL+"
    }
  }
};

const props = defineProps({
  getSurface: {
    type: Function,
    required: true
  },
  surfaceLabel: {
    type: String,
    required: true
  },
  downscaleFactor: {
    type: Number,
    required: true
  }
});

const pivotStyles = computed(() => {
  const label = props.surfaceLabel === "frontLeftChest" ? "front" : props.surfaceLabel;
  const { bottomY, topX, topY } = props.getSurface(label).surfaceLocationOffset;
  return {
    top: `${topY * props.downscaleFactor}px`,
    left: `${topX * props.downscaleFactor}px`,
    height: `${(bottomY - topY) * props.downscaleFactor}px`
  };
});

const chestStyles = computed(() => {
  const { bottomY, topX, topY } = props.getSurface("frontLeftChest").surfaceLocationOffset;
  return {
    top: `${topY * props.downscaleFactor}px`,
    left: `${topX * props.downscaleFactor}px`,
    height: `${(bottomY - topY) * props.downscaleFactor}px`
  };
});
</script>

<template>
  <div
    class="artwork-measurement"
    :class="{
      'artwork-measurement--dark': props.getSurface(props.surfaceLabel).isDark
    }"
    :style="pivotStyles"
  >
    <div class="artwork-measurement__legends">
      <div class="artwork-measurement__legend">
        <div class="artwork-measurement__legend-title">
          {{ measurements[props.surfaceLabel].legendLeft.title }}
        </div>
        <div class="artwork-measurement__legend-description-wrapper">
          <div class="artwork-measurement__legend-description">
            {{ measurements[props.surfaceLabel].legendLeft.description }}
          </div>
        </div>
      </div>
      <div class="artwork-measurement__legend">
        <div class="artwork-measurement__legend-title">
          {{ measurements[props.surfaceLabel].legendRight.title }}
        </div>
        <div class="artwork-measurement__legend-description-wrapper">
          <div class="artwork-measurement__legend-description">
            {{ measurements[props.surfaceLabel].legendRight.description }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="props.surfaceLabel === 'frontLeftChest'"
    class="artwork-measurement-chest"
    :class="{
      'artwork-measurement-chest--dark': props.getSurface(props.surfaceLabel).isDark
    }"
    :style="chestStyles"
  >
    <div class="artwork-measurement-chest__description">centered over far edge of collar</div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";

.artwork-measurement,
.artwork-measurement-chest {
  user-select: none;
  pointer-events: none;
  position: absolute;
  margin-left: -1px;
  border-right-color: currentColor;
  border-right-width: 2px;
  color: $pod-gray-modern-900;

  &--dark {
    color: $white;
  }
}
.artwork-measurement {
  border-right-style: solid;

  &::before,
  &::after {
    width: 10px;
    height: 2px;
    position: absolute;
    left: -4px;
    background-color: currentColor;
    content: "";
  }

  &::before {
    top: 0;
  }

  &::after {
    bottom: 0;
  }

  &__legends {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    text-align: center;
    position: absolute;
    left: 50%;
    top: 50%;
    white-space: nowrap;
    transform: translate(-50%, -50%);
  }

  &__legend {
    font-size: 12px;
    font-weight: 700;
  }

  &__legend-title {
    line-height: 1;
    margin-bottom: 2px;
  }

  &__legend-description-wrapper {
    border-radius: 5px;
    padding: 0 4px;
    background: currentColor;
  }

  &__legend-description {
    filter: invert(1);
    line-height: 16px;
  }
}

.artwork-measurement-chest {
  border-right-style: dashed;

  &__description {
    position: absolute;
    text-align: left;
    left: 10px;
    bottom: 24px;
    font-size: 12px;
    line-height: 1;
  }
}
</style>
